/* eslint-disable */
// attachmentRegistry — the one place that says what can be attached to a
// notification, and everything needed to search it, render it and open it.
//
// ⚠️ ADDING A FIFTH FAMILY MUST BE ONE ENTRY AND NOTHING ELSE. That is the same
// property that lets a new portal join the notification platform by INSERTing
// one row into notification_types. If you find yourself writing a fifth
// `if (kind === …)` somewhere, this registry has stopped doing its job.
//
// ⚠️ `kind` IS MATCHED AGAINST THIS OBJECT, NEVER CONCATENATED INTO A TABLE
// NAME. A payload is admin-supplied data; an unknown kind resolves to undefined
// and is skipped, it never becomes `from(kind + '_table')`.
//
// ⚠️ NOTHING HERE IS EVER WRITTEN INTO A NOTIFICATION PAYLOAD. The payload
// carries {kind, id} and nothing else — no code, no name, and above all no
// image URL. See the header of AttachmentCard.js for why that is the rule the
// whole feature is built around.

import { getDevPrimaryImage } from './devCardUtils';
import { readLensDevImages } from '../portals/data/lensBank/lensDev/useLensDevFiles';

// A row's cover image, resolved per family from the ALREADY-FETCHED row.
// Each of these is the same resolution the family's own cards use — imported,
// never re-implemented, so a change to how a dev picks its primary image
// reaches this surface for free.
const itemImage = (r) => {
  const imgs = (r?.images?.length ? r.images : r?.data?.images) || [];
  const first = imgs[0];
  const url = typeof first === 'string' ? first : (first?.url || null);
  return url || r?.data?.image || r?.image || null;
};

const lensDevImage = (r) => {
  const imgs = readLensDevImages(r) || [];
  const p = r?.custom_fields?.primaryImageIndex;
  const i = Number.isInteger(p) && p >= 0 && p < imgs.length ? p : 0;
  return imgs[i]?.url || r?.photo_original_url || null;
};

export const ATTACH_FAMILIES = [
  {
    kind: 'item',
    label: 'Original',
    group: 'Original models',
    table: 'items',
    idCol: 'id',
    // Both live inside the JSONB `data` blob; PostgREST addresses them with ->>
    // MEASURED against production, not inferred from the UI (Conv #90): an
    // `items` row is (id, data, created_at) and NO row carries data.model —
    // a model-based search would have matched nothing, silently. sku + brand
    // are what actually identify an original.
    codeCol: 'data->>sku',
    nameCol: 'data->>brand',
    select: 'id, data',
    code: (r) => r?.data?.sku || '',
    name: (r) => r?.data?.brand || '',
    detail: (r) => [r?.data?.frameCategory, r?.data?.material].filter(Boolean).join(' · '),
    image: itemImage,
    portal: 'Originals',
    tab: 'catalog',
    pill: { fg: '#3f5a8a', bg: '#eef2fa', bd: '#d8e2f2' },
  },
  {
    kind: 'dev',
    label: 'Development',
    group: 'Developments',
    table: 'developments',
    idCol: 'id',
    // ⚠️ THERE IS NO developments.dev_number COLUMN. The grid's column id says
    // otherwise (devGridColumns.js:41) and PostgREST answers a query for it
    // with 42703 — measured. The dev number lives in the JSONB blob, and
    // data.model is null on every row sampled, so designer is the name.
    codeCol: 'data->>dev_number',
    nameCol: 'data->>designer',
    select: 'id, data',
    // Conv #17 — every developments read that feeds a list filters soft-deletes.
    activeOnly: (q) => q.is('deleted_at', null),
    code: (r) => r?.data?.dev_number || '',
    name: (r) => r?.data?.designer || '',
    detail: (r) => [r?.data?.fty, r?.data?.material].filter(Boolean).join(' · '),
    image: (r) => getDevPrimaryImage(r),
    portal: 'Developments',
    tab: 'developments',
    pill: { fg: '#3f7d55', bg: '#eef6f1', bd: '#d6eadd' },
  },
  {
    kind: 'lens',
    label: 'Lens',
    group: 'Lenses',
    table: 'lens_bank',
    idCol: 'id',
    codeCol: 'lens_no',
    nameCol: 'raw_name',
    // ⚠️ WHICH COLUMN notifications.entity_id MATCHES FOR THIS FAMILY.
    // lens.favorited seeds the composer with { id: lens.lens_no } — the CODE,
    // not the primary key (LensCard.js:74) — and the composer passes that
    // straight through as p_entity_id. So the legacy pair resolves through
    // lens_no, and requestOpenLens (which also wants a code) then works
    // unchanged. Only `lens` has one: entity_type's other values are 'none',
    // 'qc_report' and 'order', and the last two have no registry family.
    entityIdCol: 'lens_no',
    select: 'id, lens_no, raw_name, color, category, photo_url',
    activeOnly: (q) => q.is('deleted_at', null),
    code: (r) => r?.lens_no || '',
    name: (r) => r?.raw_name || '',
    detail: (r) => [r?.color, r?.category != null && r?.category !== '' ? `cat.${r.category}` : ''].filter(Boolean).join(' · '),
    image: (r) => r?.photo_url || null,
    portal: 'Lens Database',
    tab: 'lensBank',
    pill: { fg: '#8a4f1d', bg: '#fdf1e4', bd: '#e8c9a8' },
  },
  {
    kind: 'lensdev',
    label: 'Lens dev',
    group: 'Lens developments',
    table: 'lens_developments',
    idCol: 'id',
    codeCol: 'ltd_code',
    nameCol: 'name',
    select: 'id, ltd_code, name, stage, factory, custom_fields, photo_original_url',
    code: (r) => r?.ltd_code || '',
    name: (r) => r?.name || '',
    detail: (r) => [r?.stage ? `Stage ${r.stage}` : '', r?.factory].filter(Boolean).join(' · '),
    image: lensDevImage,
    portal: 'Lens Database',
    tab: 'lensBank',
    pill: { fg: '#8a3f74', bg: '#f7eef5', bd: '#eddaea' },
  },
];

export const ATTACH_MAX = 4;

const BY_KIND = ATTACH_FAMILIES.reduce((m, f) => { m[f.kind] = f; return m; }, {});

// Unknown kinds return undefined and are SKIPPED by every consumer — never
// guessed at, never used to build a query.
export function familyFor(kind) {
  return Object.prototype.hasOwnProperty.call(BY_KIND, kind) ? BY_KIND[kind] : undefined;
}

// The payload's whole vocabulary: two facts per attachment.
export function toPayloadRef(row) {
  return { kind: row.kind, id: String(row.id) };
}

// The LEGACY pair. Before P6 an attachment lived in entity_type + entity_id,
// and lens.favorited — the one notification that has always carried an item —
// still does. P6's renderer read only payload.attachments, so that notification
// was the only one WITHOUT a picture. That was a defect, not a decision.
//
// Returns a single ref, or null when there is nothing renderable: entity_type
// 'none' (no item), or a type with no registry family ('qc_report', 'order').
// Those keep rendering exactly as they do today — nothing is invented for them.
export function legacyAttachment(entityType, entityId) {
  if (!entityType || entityType === 'none' || !entityId) return null;
  const fam = familyFor(entityType);
  if (!fam || !fam.entityIdCol) return null;
  return { kind: fam.kind, id: String(entityId), byEntityId: true };
}

// Read attachments off a notification, tolerating anything. Returns [] rather
// than throwing on a malformed payload — a bad payload must never blank a
// message that is otherwise fine.
export function readAttachments(payload) {
  const raw = payload && typeof payload === 'object' ? payload.attachments : null;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a) => a && typeof a === 'object' && familyFor(a.kind) && a.id != null)
    .map((a) => ({ kind: a.kind, id: String(a.id) }))
    .slice(0, ATTACH_MAX);
}
